import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  Mail, 
  User, 
  Calendar, 
  Briefcase, 
  MessageSquare,
  Inbox,
  CheckCircle,
  Clock,
  Trash2,
  RefreshCw,
  Receipt,
  LogOut
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { getAdminToken, clearAdminToken } from "@/pages/AdminLoginPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4 }
};

export default function AdminPage() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [adminUser, setAdminUser] = useState(null);

  const verifyAuth = async () => {
    const token = getAdminToken();
    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      const res = await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminUser(res.data.username);
    } catch (error) {
      clearAdminToken();
      navigate("/admin/login");
    }
  };

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contact`);
      setSubmissions(response.data);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const updateSubmissionStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}?status=${status}`);
      toast.success(`Marked as ${status}`);
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm("Are you sure you want to delete this submission?")) return;
    try {
      await axios.delete(`${API}/contact/${id}`);
      toast.success("Submission deleted");
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      toast.error("Failed to delete submission");
    }
  };

  useEffect(() => {
    verifyAuth();
    fetchSubmissions();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: "bg-[#D4FF33] text-[#0A3F2F]",
      read: "bg-[#E0E0D8] text-[#4A5F55]",
      replied: "bg-[#0A3F2F] text-white"
    };
    const icons = {
      new: <Clock size={12} />,
      read: <CheckCircle size={12} />,
      replied: <Mail size={12} />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>
        {icons[status] || icons.new}
        {status || 'new'}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <header className="bg-[#0A3F2F] text-white py-4 px-4 md:px-8 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              data-testid="admin-back-link"
            >
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Back to Site</span>
            </Link>
            <div className="h-6 w-px bg-white/20 hidden sm:block" />
            <h1 className="font-['Syne'] text-lg md:text-xl font-bold">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            {adminUser && (
              <span className="text-white/70 text-sm hidden sm:inline">
                Welcome, {adminUser}
              </span>
            )}
            <button
              onClick={fetchSubmissions}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-2 rounded-lg transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-500/20 hover:bg-red-500/30 text-red-200 px-3 py-2 rounded-lg transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Quick Links */}
        <motion.div className="mb-6 flex flex-wrap gap-3" {...fadeUp}>
          <Link
            to="/admin/invoices"
            className="inline-flex items-center gap-3 bg-[#0A3F2F] text-white px-5 py-3 rounded-xl hover:bg-[#0d5139] transition-colors"
            data-testid="invoices-link"
          >
            <Receipt size={20} />
            <span className="font-medium">Manage Invoices</span>
          </Link>
          <Link
            to="/admin/settings"
            className="inline-flex items-center gap-3 bg-[#EBEBE3] text-[#0A3F2F] px-5 py-3 rounded-xl hover:bg-[#D1D1C7] transition-colors"
            data-testid="settings-link"
          >
            <Mail size={20} />
            <span className="font-medium">Site Settings</span>
          </Link>
        </motion.div>

        {/* Stats */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8"
          {...fadeUp}
        >
          <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#D4FF33] flex items-center justify-center">
                <Inbox size={20} className="text-[#0A3F2F]" />
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-bold text-[#0A3F2F]">{submissions.length}</p>
                <p className="text-xs md:text-sm text-[#4A5F55]">Total</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#0A3F2F] flex items-center justify-center">
                <Clock size={20} className="text-[#D4FF33]" />
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-bold text-[#0A3F2F]">
                  {submissions.filter(s => s.status === 'new' || !s.status).length}
                </p>
                <p className="text-xs md:text-sm text-[#4A5F55]">New</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm hidden md:block">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#EBEBE3] flex items-center justify-center">
                <CheckCircle size={20} className="text-[#0A3F2F]" />
              </div>
              <div>
                <p className="text-3xl font-bold text-[#0A3F2F]">
                  {submissions.filter(s => s.status === 'read').length}
                </p>
                <p className="text-sm text-[#4A5F55]">Read</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm hidden md:block">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#EBEBE3] flex items-center justify-center">
                <Mail size={20} className="text-[#0A3F2F]" />
              </div>
              <div>
                <p className="text-3xl font-bold text-[#0A3F2F]">
                  {submissions.filter(s => s.status === 'replied').length}
                </p>
                <p className="text-sm text-[#4A5F55]">Replied</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Submissions List */}
        <motion.div 
          className="bg-white rounded-xl shadow-sm overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <div className="p-4 md:p-6 border-b border-[#E0E0D8]">
            <h2 className="font-['Syne'] text-xl font-bold text-[#0A3F2F]" data-testid="submissions-title">
              Contact Submissions
            </h2>
          </div>

          {loading ? (
            <div className="p-8 md:p-12 text-center">
              <RefreshCw size={32} className="animate-spin text-[#0A3F2F] mx-auto mb-4" />
              <p className="text-[#4A5F55]">Loading submissions...</p>
            </div>
          ) : submissions.length === 0 ? (
            <div className="p-8 md:p-12 text-center" data-testid="no-submissions">
              <div className="w-16 h-16 rounded-full bg-[#EBEBE3] flex items-center justify-center mx-auto mb-4">
                <Inbox size={32} className="text-[#4A5F55]" />
              </div>
              <h3 className="font-['Syne'] text-lg font-bold text-[#0A3F2F] mb-2">No submissions yet</h3>
              <p className="text-[#4A5F55]">Contact form submissions will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E0E0D8]">
              {submissions.map((submission, index) => (
                <motion.div
                  key={submission.id}
                  className="p-4 md:p-6 hover:bg-[#F5F5F0] cursor-pointer transition-colors"
                  onClick={() => setSelectedSubmission(selectedSubmission?.id === submission.id ? null : submission)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  data-testid={`submission-${index}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
                    <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
                      <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#0A3F2F] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-sm md:text-base">
                          {submission.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-semibold text-[#0A3F2F] truncate">{submission.name}</h3>
                          {getStatusBadge(submission.status)}
                        </div>
                        <p className="text-sm text-[#4A5F55] truncate">{submission.email}</p>
                        {submission.service && (
                          <p className="text-xs text-[#4A5F55] mt-1 flex items-center gap-1">
                            <Briefcase size={12} />
                            {submission.service}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#4A5F55] md:text-right">
                      <Calendar size={12} />
                      {formatDate(submission.created_at)}
                    </div>
                  </div>

                  {/* Expanded Message */}
                  {selectedSubmission?.id === submission.id && (
                    <motion.div
                      className="mt-4 pt-4 border-t border-[#E0E0D8]"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="flex items-start gap-2 mb-4">
                        <MessageSquare size={16} className="text-[#4A5F55] mt-0.5 flex-shrink-0" />
                        <p className="text-[#0A3F2F] text-sm leading-relaxed">{submission.message}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href={`mailto:${submission.email}?subject=Re: Your inquiry to Streamcaster Studios`}
                          className="inline-flex items-center gap-2 bg-[#0A3F2F] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#0d5139] transition-colors"
                          onClick={(e) => { e.stopPropagation(); updateSubmissionStatus(submission.id, 'replied'); }}
                          data-testid={`reply-btn-${index}`}
                        >
                          <Mail size={14} />
                          Reply
                        </a>
                        {submission.status !== 'read' && submission.status !== 'replied' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); updateSubmissionStatus(submission.id, 'read'); }}
                            className="inline-flex items-center gap-2 bg-[#EBEBE3] text-[#0A3F2F] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#D1D1C7] transition-colors"
                            data-testid={`mark-read-btn-${index}`}
                          >
                            <CheckCircle size={14} />
                            Mark Read
                          </button>
                        )}
                        {submission.status === 'read' && (
                          <button
                            onClick={(e) => { e.stopPropagation(); updateSubmissionStatus(submission.id, 'replied'); }}
                            className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-green-200 transition-colors"
                            data-testid={`mark-replied-btn-${index}`}
                          >
                            <CheckCircle size={14} />
                            Mark Replied
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteSubmission(submission.id); }}
                          className="inline-flex items-center gap-2 bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-medium hover:bg-red-200 transition-colors"
                          data-testid={`delete-btn-${index}`}
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
